import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  despliegueDeEstacion,
  posterDesignOf,
  xurveyApi,
  type PosterPreset,
  type StylePreset,
  type XurveyDeployment,
} from '@/lib/api/xurvey';
import type { XkEvent, XkEventGroup, XkStation } from '@/lib/api/types';
import { useT } from '@/lib/i18n/useT';
import { esSesionCaida, motivoDeError } from './resultadoDeGuardado';

/**
 * Cómo le fue a cada evento elegido.
 *
 * `fallido` si algo falló en él, aunque se aplicara en otras de sus estaciones:
 * lo que importa es que ese evento no quedó igual que el resto.
 */
interface ResultadoEvento {
  eventId: number;
  estado: 'aplicado' | 'sinDespliegue' | 'fallido';
  despliegues: number;
  /** El primer error de ese evento. */
  error?: unknown;
}

/**
 * Aplicar un estilo guardado a OTROS eventos, sin abrirlos uno a uno.
 *
 * Guardar un estilo ya funcionaba; reutilizarlo obligaba a ir al destino, abrir
 * su Design Journey y pulsarlo allí. Con una gira de doce fechas eso son doce
 * viajes para repetir doce veces lo mismo, y es justo lo que el estilo existía
 * para evitar.
 *
 * **Escribe SÓLO apariencia.** De los `overrides` de cada despliegue toca
 * `design` y `styleOverrides`, y deja intactos `hiddenStepIds`, `stepOrder`,
 * `hiddenComponentIds` y `poster`. Un estilo que arrastrara la estructura
 * señalaría pasos que en la encuesta de destino no existen —o, peor, existen y
 * son otros—, y el evento se quedaría escondiendo preguntas al azar.
 *
 * Va evento a evento y no en una llamada: no hay endpoint de lote, y fingir uno
 * en el cliente escondería que un fallo a mitad deja el trabajo hecho a medias.
 * Aquí se cuenta lo que se aplicó y lo que no — y de verdad: antes el primer
 * fallo cortaba la fila y los eventos de detrás ni se intentaban. Ahora un
 * evento que falla no para a los demás, y si alguno falla el diálogo se queda
 * abierto con sólo los fallidos marcados y el porqué debajo de cada uno, listo
 * para reintentar en cuanto se arregle (épica #333, XCS-15). Sí puede fallar de
 * verdad: se reenvían los `overrides` que ya tenía el despliegue, y si entre
 * ellos va `consent`, el servidor revisa la capa legal entera.
 *
 * Con `tipo="poster"` hace lo mismo con un diseño de cartel: funde sólo las
 * claves de diseño en `overrides.poster` y deja el texto de cada evento. El
 * diálogo es el mismo porque el gesto es el mismo —elegir eventos y aplicar—
 * y lo único que cambia es qué parte del despliegue se escribe.
 */
export function ApplyStyleDialog({
  preset,
  onClose,
  tipo = 'style',
}: {
  /** El estilo o diseño de cartel a aplicar. `null` cierra el diálogo. */
  preset: StylePreset | PosterPreset | null;
  onClose: () => void;
  /** Qué se escribe en cada despliegue: la apariencia o el cartel. */
  tipo?: 'style' | 'poster';
}) {
  const t = useT();
  const qc = useQueryClient();
  const [elegidos, setElegidos] = useState<Set<number>>(new Set());
  /** Por qué no se aplicó en cada evento fallido del último intento (id → motivo). */
  const [motivos, setMotivos] = useState<Map<number, string>>(new Map());
  const abierto = preset !== null;

  const proyectosQ = useQuery({
    queryKey: queryKeys.eventGroups.all,
    queryFn: () => eventsApi.listGroups(),
    enabled: abierto,
  });
  const eventosQ = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => eventsApi.listEvents(),
    enabled: abierto,
  });

  /** Los eventos colgando de su proyecto. Los huérfanos van al final, juntos. */
  const porProyecto = useMemo(() => {
    const proyectos = (proyectosQ.data?.items ?? []) as XkEventGroup[];
    const eventos = (eventosQ.data?.items ?? []) as XkEvent[];
    const mapa = new Map<number | null, { nombre: string; eventos: XkEvent[] }>();
    for (const p of proyectos) mapa.set(p.id, { nombre: p.name, eventos: [] });
    for (const e of eventos) {
      const clave = e.eventGroupId ?? null;
      if (!mapa.has(clave)) {
        /*
         * Su proyecto no está en la lista visible — o no lo tiene.
         *
         * Pasa de verdad: un evento puede colgar de un proyecto que este
         * usuario no ve. Se agrupaban bajo `#1`, que no le dice nada a nadie y
         * parece un error. El evento SÍ es suyo y se puede elegir, así que se
         * enseña; lo que se calla es un id que no significa nada.
         */
        mapa.set(clave, { nombre: t('applyStyle.noProject'), eventos: [] });
      }
      mapa.get(clave)!.eventos.push(e);
    }
    // Un proyecto sin eventos no se puede elegir: no tiene dónde aplicarse.
    return [...mapa.entries()].filter(([, v]) => v.eventos.length > 0);
  }, [proyectosQ.data, eventosQ.data, t]);

  const alternar = (id: number) =>
    setElegidos((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });

  const alternarProyecto = (eventos: XkEvent[]) => {
    const ids = eventos.map((e) => e.id);
    const todos = ids.every((id) => elegidos.has(id));
    setElegidos((prev) => {
      const siguiente = new Set(prev);
      for (const id of ids) {
        if (todos) siguiente.delete(id);
        else siguiente.add(id);
      }
      return siguiente;
    });
  };

  // Prefijo de ids para enlazar cada casilla con su nombre y su motivo.
  const prefijo = useId();
  const idDe = (id: number) => `${prefijo}-ev-${id}`;
  const nombreIdDe = (id: number) => `${prefijo}-ev-${id}-nombre`;
  const motivoIdDe = (id: number) => `${prefijo}-ev-${id}-motivo`;

  const aplicar = useMutation({
    onMutate: () => setMotivos(new Map()),
    mutationFn: async () => {
      if (!preset) return { porEvento: [] as ResultadoEvento[] };
      const porEvento: ResultadoEvento[] = [];
      // Los despliegues se piden POR ENCUESTA, así que se cachean: una gira
      // entera suele correr la misma, y sin esto sería una petición por evento
      // para recibir la misma lista.
      const porEncuesta = new Map<number, XurveyDeployment[]>();

      const ids = [...elegidos];
      // Una sesión caída (401 final) corta todo: seguir sólo repetiría el
      // refresco y el cierre de sesión que el interceptor ya hizo.
      let sesionCaida: unknown = null;
      for (const eventId of ids) {
        if (sesionCaida !== null) {
          porEvento.push({ eventId, estado: 'fallido', despliegues: 0, error: sesionCaida });
          continue;
        }
        let tocados = 0;
        // Se guarda sólo el primer error del evento, pero NO se para: ni este
        // evento (sus otras estaciones) ni los de detrás.
        let fallo = false;
        let error: unknown;
        const anotar = (e: unknown) => {
          if (!fallo) error = e;
          fallo = true;
          if (esSesionCaida(e)) sesionCaida = e;
        };

        let estaciones: XkStation[] = [];
        try {
          estaciones = ((await eventsApi.listStations({ eventId })).items ?? []) as XkStation[];
        } catch (e) {
          anotar(e);
        }
        for (const estacion of estaciones) {
          if (sesionCaida !== null) break;
          if (!estacion.surveysId) continue;
          try {
            if (!porEncuesta.has(estacion.surveysId)) {
              porEncuesta.set(estacion.surveysId, await xurveyApi.listDeployments(estacion.surveysId));
            }
            // El real y activo de la estación, no el primero que case: escribir
            // el estilo en un QR de prueba caducado no cambia nada visible.
            const suyo = despliegueDeEstacion(porEncuesta.get(estacion.surveysId), estacion.id);
            if (!suyo) continue;
            const actuales = suyo.overrides ?? {};
            await xurveyApi.updateDeployment(suyo.id, {
              overrides:
                tipo === 'poster'
                  ? {
                      // Lo que ya tenía se conserva: sólo el diseño del cartel,
                      // y encima del texto que ese evento ya tenía.
                      ...actuales,
                      poster: {
                        ...((actuales.poster ?? {}) as Record<string, unknown>),
                        ...posterDesignOf(((preset as PosterPreset).poster ?? {}) as Record<string, unknown>),
                      },
                      posterPresetId: preset.id,
                    }
                  : {
                      // Lo que ya tenía se conserva: aquí sólo se pisa la apariencia.
                      ...actuales,
                      design: ((preset as StylePreset).design ?? {}) as Record<string, unknown>,
                      styleOverrides: ((preset as StylePreset).styleOverrides ?? {}) as Record<string, string>,
                    },
            });
            tocados += 1;
          } catch (e) {
            anotar(e);
          }
        }
        porEvento.push({
          eventId,
          estado: fallo ? 'fallido' : tocados > 0 ? 'aplicado' : 'sinDespliegue',
          despliegues: tocados,
          ...(fallo ? { error } : {}),
        });
      }
      return { porEvento };
    },
    onSuccess: ({ porEvento }) => {
      void qc.invalidateQueries({ queryKey: ['xurvey', 'deployments'] });
      const aplicados = porEvento.filter((r) => r.estado === 'aplicado');
      const sinDespliegue = porEvento.filter((r) => r.estado === 'sinDespliegue');
      const fallidos = porEvento.filter((r) => r.estado === 'fallido');

      if (fallidos.length > 0) {
        /*
         * Se queda ABIERTO, con sólo los que fallaron marcados.
         *
         * El toast dura cuatro segundos y es texto plano: no cabe una lista de
         * eventos. El detalle va aquí, debajo de cada nombre, y la selección
         * queda lista para volver a pulsar «Aplicar» cuando se arregle lo que
         * faltaba. Los aplicados y los que no tenían encuesta se desmarcan:
         * reaplicarlos no cambiaría nada.
         */
        setMotivos(new Map(fallidos.map((r) => [r.eventId, motivoDeError(r.error, t)])));
        setElegidos(new Set(fallidos.map((r) => r.eventId)));
        const conteo = { ok: aplicados.length, total: porEvento.length };
        toast({
          tone: 'error',
          title: tipo === 'poster' ? t('applyPoster.partial', conteo) : t('applyStyle.partial', conteo),
          description:
            t('applyStyle.partialHint') +
            (sinDespliegue.length
              ? ` ${t('applyStyle.skipped', { n: String(sinDespliegue.length) })}`
              : ''),
        });
        return;
      }

      setMotivos(new Map());
      const despliegues = aplicados.reduce((suma, r) => suma + r.despliegues, 0);
      if (despliegues === 0) {
        // No es un éxito silencioso: se eligieron eventos y no se tocó nada.
        toast({ tone: 'error', title: tipo === 'poster' ? t('applyPoster.noneApplied') : t('applyStyle.noneApplied') });
        return;
      }
      const n = String(despliegues);
      const e = String(aplicados.length);
      toast({
        tone: 'success',
        title: tipo === 'poster' ? t('applyPoster.applied', { n, e }) : t('applyStyle.applied', { n, e }),
        // Lo que se quedó fuera se DICE. Un recuento que sólo cuenta los
        // aciertos deja creyendo que la gira entera quedó igualada.
        ...(sinDespliegue.length
          ? { description: t('applyStyle.skipped', { n: String(sinDespliegue.length) }) }
          : {}),
      });
      cerrar();
    },
    onError: (e: unknown) =>
      toast({
        tone: 'error',
        title: tipo === 'poster' ? t('applyPoster.failed') : t('applyStyle.failed'),
        description: motivoDeError(e, t),
      }),
  });

  const cerrar = () => {
    setElegidos(new Set());
    setMotivos(new Map());
    onClose();
  };

  const cargando = proyectosQ.isLoading || eventosQ.isLoading;

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && cerrar()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {tipo === 'poster'
              ? t('applyPoster.title', { name: preset?.name ?? '' })
              : t('applyStyle.title', { name: preset?.name ?? '' })}
          </DialogTitle>
          <DialogDescription>
            {tipo === 'poster' ? t('applyPoster.description') : t('applyStyle.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 max-h-[46vh] overflow-y-auto rounded-lg border border-[var(--color-border-default)]">
          {cargando ? (
            <p className="p-6 text-center text-sm text-[var(--color-fg-muted)]">
              {t('common.loading')}
            </p>
          ) : porProyecto.length === 0 ? (
            <p className="p-6 text-center text-sm text-[var(--color-fg-muted)]">
              {t('applyStyle.noEvents')}
            </p>
          ) : (
            porProyecto.map(([clave, grupo]) => {
              const ids = grupo.eventos.map((e) => e.id);
              const todos = ids.every((id) => elegidos.has(id));
              return (
                <div key={String(clave)} className="border-b border-[var(--color-border-subtle)] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => alternarProyecto(grupo.eventos)}
                    className="flex w-full items-center gap-2 bg-[var(--color-bg-elevated)] px-3 py-2 text-left"
                  >
                    <span className="flex-1 truncate text-xs font-bold uppercase tracking-wide text-[var(--color-fg-muted)]">
                      {grupo.nombre}
                    </span>
                    <span className="text-[11px] text-[var(--color-accent-ink)]">
                      {todos ? t('applyStyle.noneOfProject') : t('applyStyle.allOfProject')}
                    </span>
                  </button>
                  {grupo.eventos.map((e) => {
                    const motivo = motivos.get(e.id);
                    return (
                      <label
                        key={e.id}
                        htmlFor={idDe(e.id)}
                        className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-[var(--color-bg-elevated)]"
                      >
                        <input
                          type="checkbox"
                          id={idDe(e.id)}
                          aria-labelledby={nombreIdDe(e.id)}
                          aria-describedby={motivo ? motivoIdDe(e.id) : undefined}
                          checked={elegidos.has(e.id)}
                          onChange={() => alternar(e.id)}
                          className="h-4 w-4 accent-[var(--color-accent)]"
                        />
                        {/* Toda la fila es la etiqueta, así que un clic en el
                            relleno o en el motivo también marca. El nombre
                            accesible de la casilla es sólo el nombre del evento
                            (aria-labelledby); el motivo la describe. */}
                        <span className="min-w-0 flex-1">
                          <span
                            id={nombreIdDe(e.id)}
                            className="block truncate text-sm text-[var(--color-fg-primary)]"
                          >
                            {e.name}
                          </span>
                          {motivo ? (
                            <span
                              id={motivoIdDe(e.id)}
                              className="block text-xs text-[var(--color-error)]"
                            >
                              {motivo}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={cerrar} disabled={aplicar.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => aplicar.mutate()}
            disabled={elegidos.size === 0 || aplicar.isPending}
            leftIcon={
              aplicar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )
            }
          >
            {t('applyStyle.cta', { n: String(elegidos.size) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
